import * as i0 from '@angular/core';
import { Directive, Inject, Input } from '@angular/core';
import { Subject, fromEvent, race, timer } from 'rxjs';
import { scan, shareReplay, pluck, distinctUntilChanged, map, takeUntil, mapTo, take } from 'rxjs/operators';
import { WINDOW } from '@slidebean/hydra/web-apis';

class AvatarPrimitive {
    constructor(window) {
        this.window = window;
        this.destroy$ = new Subject();
        this.action$ = new Subject();
        this.state$ = this.action$.pipe(scan((state, action) => {
            switch (action?.type) {
                case 'fallback':
                    return {
                        status: state.status === 'loaded' && action.replaceLoaded
                            ? 'idle'
                            : state.status,
                        fallback: true,
                    };
                case 'loading':
                    return { status: 'loading', fallback: action.fallback };
                case 'loaded':
                    return { status: 'loaded', fallback: false };
                case 'error':
                    return { status: 'error', fallback: true };
                default:
                    return state;
            }
        }, { status: 'idle', fallback: false }), shareReplay(1));
        this.status$ = this.state$.pipe(pluck('status'), distinctUntilChanged());
        this.content$ = this.state$.pipe(map(state => {
            if (state.status === 'loaded')
                return 'image';
            return state.fallback ? 'fallback' : 'none';
        }), distinctUntilChanged());
        // Add subscription to make sure the state is updated properly.
        this.state$.pipe(takeUntil(this.destroy$)).subscribe();
    }
    ngOnInit() {
        this.use(this.src, this.getFallbackDelay());
    }
    ngOnChanges(changes) {
        const src = changes['src'];
        if (!!src &&
            src.currentValue !== src.previousValue &&
            !src.isFirstChange()) {
            this.use(this.src, this.getFallbackDelay());
        }
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    use(src, delay) {
        if (!src) {
            this.action$.next({ type: 'fallback', replaceLoaded: true });
            return;
        }
        this.action$.next({ type: 'loading', fallback: delay === void 0 });
        const image = new this.window.Image();
        const load$ = fromEvent(image, 'load').pipe(mapTo('loaded'));
        const error$ = fromEvent(image, 'error').pipe(mapTo('error'));
        race(load$, error$)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(type => this.action$.next({ type }));
        image.src = src;
        if (!!delay) {
            timer(delay)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.action$.next({ type: 'fallback' }));
        }
    }
    getFallbackDelay() {
        return void 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AvatarPrimitive, deps: [{ token: WINDOW }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: AvatarPrimitive, inputs: { src: "src" }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AvatarPrimitive, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }], propDecorators: { src: [{
                type: Input
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { AvatarPrimitive };
